import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { ButtonSize, ButtonType } from '../../atoms/baseButton/BaseButton'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { DropdownButton, DropdownComponentProps } from '../../atoms/dropdownButton/DropdownButton'
import { RadioButton } from '../../atoms/formElements/RadioButtons'
import { BodyTextSize, DisplaySize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

/**
 * One AI conversation the provider can turn on or off for the client, e.g. the mental health
 * coaching conversation or the manager growth coaching conversation.
 */
export type AIConversationToggleRow = {
  /** Stable identifier used for React keys and test IDs, e.g. the conversation type. */
  id: string
  /** Visible row label, also read into the ON/OFF control's accessibility label. */
  label: string
  isOn: boolean
  onChange: (isOn: boolean) => void
  /**
   * ISO calendar date (YYYY-MM-DD) on which the client's access ends. Rendered as
   * "Expires on …" while the row is on, or "Expired on …" once `isExpired` is set.
   */
  expirationDate?: string
  /** When true the control is hidden and the row renders in the inactive text color. */
  isExpired?: boolean
}

type AICoachToggleProps = {
  rows: AIConversationToggleRow[]
}

const TOGGLE_ON_VALUE = 'on'
const TOGGLE_OFF_VALUE = 'off'

const Container = styled.View(({ theme }) => ({
  borderWidth: 1,
  borderColor: theme.colors.borderDefault,
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: theme.spacing['16px'],
  padding: theme.spacing['24px'],
  width: !theme.breakpoints.isMinWidthLaptop ? '100%' : '288px',
  gap: theme.spacing['16px'],
}))

const Row = styled.View<{ zIndex: number }>(({ theme, zIndex }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  zIndex,
}))

const RowLabels = styled.View({
  flex: 1,
})

const RadioMenu = styled.View(({ theme }) => ({
  gap: theme.spacing['8px'],
  padding: theme.spacing['24px'],
}))

type ToggleRadioMenuProps = DropdownComponentProps & {
  options: Array<{ label: string; value: string }>
  selectedValue: string
}

const ToggleRadioMenu: React.FC<ToggleRadioMenuProps> = ({ onOptionPress, options, selectedValue }) => (
  <RadioMenu>
    {options.map((option) => (
      <RadioButton
        buttonStyle={{ marginBottom: 0 }}
        buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
        gridButtonsGap={0}
        key={option.value}
        onChange={() => onOptionPress(option.value, { id: option.value, selectHandler: noop, text: option.label })}
        parentContainerWidth={120}
        selected={selectedValue === option.value}
        title={option.label}
        value={option.value}
        widthWrapToContent
      />
    ))}
  </RadioMenu>
)

export const AIConversationToggleRowItem: React.FC<{ row: AIConversationToggleRow; zIndex: number }> = ({
  row,
  zIndex,
}) => {
  const { colors, spacing } = useTheme()
  const { formatDate, formatMessage } = useIntl()
  const { id, label, isOn, onChange, expirationDate, isExpired = false } = row

  const onLabel = formatMessage({ defaultMessage: 'On', description: 'Dropdown option that turns Lyra AI on' })
  const offLabel = formatMessage({ defaultMessage: 'Off', description: 'Dropdown option that turns Lyra AI off' })
  const options = [
    { label: offLabel, value: TOGGLE_OFF_VALUE },
    { label: onLabel, value: TOGGLE_ON_VALUE },
  ]
  const selectedValue = isOn ? TOGGLE_ON_VALUE : TOGGLE_OFF_VALUE
  const textColor = isExpired ? colors.textInactive : colors.textSecondary

  const formattedExpirationDate = expirationDate
    ? formatDate(expirationDate, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : undefined
  const showExpiration = !!formattedExpirationDate && (isExpired || isOn)

  return (
    <Row testID={tID(`AICoachToggle-row-${id}`)} zIndex={zIndex}>
      <RowLabels>
        <BodyText size={BodyTextSize.SMALL} color={textColor} text={label} />
        {showExpiration && (
          <BodyText
            testID={tID(`AICoachToggle-${id}-expiration`)}
            size={BodyTextSize.CAPTION}
            color={textColor}
            text={
              isExpired ? (
                <FormattedMessage
                  defaultMessage='Expired on {date}'
                  description='Date the client lost access to a Lyra AI conversation'
                  values={{ date: formattedExpirationDate }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage='Expires on {date}'
                  description='Date the client will lose access to a Lyra AI conversation'
                  values={{ date: formattedExpirationDate }}
                />
              )
            }
          />
        )}
      </RowLabels>
      {!isExpired && (
        <DropdownButton
          buttonSize={ButtonSize.EXTRA_SMALL}
          buttonType={ButtonType.TERTIARY}
          customButtonText={isOn ? onLabel : offLabel}
          customTextStyle={{ textTransform: 'uppercase' }}
          customDropdownMenuComponent={ToggleRadioMenu}
          customDropdownMenuComponentProps={{ options, selectedValue }}
          customTextColor={isOn ? colors.textActive : colors.textSecondary}
          customTextSize={BodyTextSize.BADGE}
          dropdownItems={options.map((option) => ({
            id: option.value,
            text: option.label,
            dropdownOptionText: option.value,
            selectHandler: () => onChange(option.value === TOGGLE_ON_VALUE),
          }))}
          dropdownStyle={{ borderRadius: spacing['16px'], paddingVertical: 0, right: 0 }}
          dropdownTestID={`AICoachToggle-${id}-menu`}
          dropdownWidth='max-content'
          dropdownAccessibilityLabel={formatMessage(
            {
              defaultMessage: 'Turn Lyra AI on or off for {conversationType}',
              description:
                'Accessibility label for the on/off dropdown of one Lyra AI conversation, e.g. "Turn Lyra AI on or off for Manager growth coaching"',
            },
            { conversationType: label },
          )}
          shouldCloseMenuOnOptionPress
          testID={`AICoachToggle-${id}-dropdown`}
          triggerTestID={tID(`AICoachToggle-${id}-toggle-${isOn}`)}
          style={{
            backgroundColor: isOn ? colors.backgroundHighlightPrimary : colors.backgroundSecondary,
            borderRadius: 100,
            height: 32,
            paddingLeft: spacing['12px'],
            paddingRight: spacing['8px'],
          }}
        />
      )}
    </Row>
  )
}

/**
 * Card on the client profile listing each Lyra AI conversation the provider can enable for the
 * client. Every row is an ON/OFF dropdown; rows tied to a seat-based license also show when
 * access expires.
 */
export const AICoachToggle: React.FC<AICoachToggleProps> = ({ rows }) => (
  <Container testID={tID('AICoachToggle')}>
    <Display
      size={DisplaySize.EYEBROW}
      text={
        <FormattedMessage
          defaultMessage='Conversations with Lyra AI'
          description='Heading for the card where providers turn Lyra AI conversations on or off for a client'
        />
      }
    />
    {rows.map((row, index) => (
      <AIConversationToggleRowItem key={row.id} row={row} zIndex={rows.length - index} />
    ))}
  </Container>
)
